import type { RawQuestionSettings } from '@/features/content/useComponent';

// The single definition of how a course-level default and a component-level
// override combine.
//
// PER-KEY, component over course, skipping only null/undefined. That precision
// matters: a component that omits a key inherits the course's value, while a
// component that writes an explicit 0 genuinely turns the feature OFF and
// overrides a course-level 1. Whole-object `?? ` fallback cannot express either
// — any component settings object at all would shadow the entire course object,
// which is how the control panel could show "No" for a setting whose questions
// were rendering "Yes".
export function mergeQuestionSettings(
  courseSettings: RawQuestionSettings | null | undefined,
  componentSettings: RawQuestionSettings | null | undefined,
): RawQuestionSettings | null {
  if (!courseSettings) {
    return componentSettings ?? null;
  }

  if (!componentSettings) {
    return courseSettings;
  }

  return {
    ...courseSettings,
    ...Object.fromEntries(
      Object.entries(componentSettings).filter(
        ([, value]) => value !== null && value !== undefined,
      ),
    ),
  };
}

// Read a 0/1 AI flag, tolerating snake_case, camelCase, and booleans. Returns
// undefined when the setting is absent — the caller decides what inheriting or
// defaulting means, which is the difference between "off" and "not set".
export function readAiFlag(
  settings: RawQuestionSettings | null | undefined,
  snakeKey: string,
  camelKey: string,
): boolean | undefined {
  if (!settings) {
    return undefined;
  }

  const raw = (settings as Record<string, unknown>)[snakeKey] ??
    (settings as Record<string, unknown>)[camelKey];

  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }

  if (typeof raw === 'boolean') {
    return raw;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed === 1 : undefined;
}

// Whether AI-generated hints are enabled.
export function resolveAllowAiHints(
  settings: RawQuestionSettings | null | undefined,
): boolean {
  return readAiFlag(settings, 'allow_ai_hints', 'allowAiHints') ?? false;
}

// Whether AI practice problems are enabled for ONE level of settings.
//
// BACK-COMPAT: practice problems had no setting of their own and were enabled by
// allow_ai_hints, so an unset practice flag follows the hint flag of the SAME
// level. Use mergeAiQuestionSettings to combine a course with a component —
// applying this fallback to merged settings reads the component's hint flag as
// if it were a practice setting, which makes "use the course default" resolve to
// something the course never said.
export function resolveAllowAiPracticeProblems(
  settings: RawQuestionSettings | null | undefined,
): boolean {
  return (
    readAiFlag(settings, 'allow_ai_practice_problems', 'allowAiPracticeProblems') ??
    resolveAllowAiHints(settings)
  );
}

// Merge for the AI settings, resolving the practice flag ACROSS levels before it
// can be read back.
//
// The component's own value wins when it has one. Otherwise the value is the
// course's resolved practice setting — which is where the legacy
// "follow allow_ai_hints" fallback applies, and only there. A chapter that turns
// AI hints on must NOT thereby turn practice problems on when the instructor
// chose to inherit practice: the chapter dialog labels that choice with the
// course's resolved value, and behaviour has to match the label.
//
// The resolved flag is written into the merged object so every downstream reader
// sees an explicit value and no fallback can fire again.
export function mergeAiQuestionSettings(
  courseSettings: RawQuestionSettings | null | undefined,
  componentSettings: RawQuestionSettings | null | undefined,
): RawQuestionSettings | null {
  const merged = mergeQuestionSettings(courseSettings, componentSettings);

  if (!merged) {
    return null;
  }

  const componentPractice = readAiFlag(
    componentSettings,
    'allow_ai_practice_problems',
    'allowAiPracticeProblems',
  );
  const allowPractice =
    componentPractice ?? resolveAllowAiPracticeProblems(courseSettings);

  return {
    ...merged,
    allow_ai_practice_problems: allowPractice ? 1 : 0,
    allowAiPracticeProblems: allowPractice ? 1 : 0,
  } as RawQuestionSettings;
}

// Whether the chapter AI assistant (Kai chat) is enabled.
export function resolveAllowAiAssistant(
  settings: RawQuestionSettings | null | undefined,
): boolean {
  return readAiFlag(settings, 'allow_ai_assistant', 'allowAiAssistant') ?? false;
}

// Attempts required before an AI hint is offered. INDEPENDENT of the authored
// hint threshold: unset or <= 0 means available immediately. It deliberately
// does not copy the authored-hint meaning of 0 ("only alongside solutions"),
// which silently hid AI hints for every course that never changed the default.
export function resolveAiHintThreshold(
  settings: RawQuestionSettings | null | undefined,
): number {
  if (!settings) {
    return 0;
  }

  const raw = (settings as Record<string, unknown>).num_attempts_to_show_ai_hints ??
    (settings as Record<string, unknown>).numAttemptsToShowAiHints;

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
